import React, { memo, useCallback, useMemo, useState } from 'react'
import { CorporateAction, Type, typeFromJSON } from '../../proto/generated/corporate-action'
import { Avatar, AvatarProps, Box, Card, CardContent, CardHeader, Popover, Typography, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectTimezone } from '../../selectors/common'
import Chart from './chart'
import { zonedTime } from '../../util/chartUtils'
import { ContractSwitch, ContractualEventType } from './chart.typings'
import { binarySearchFirstDuplicate } from '../../util/general'
import { convertToUTC } from '../../util/dateTime'
import Contract from '../../assets/icons/contractSwitchIcon'
import { dividendTypeMap } from '../../assets/constants'
import { CompanyEventProto } from '../../proto/generated/company_event'


export type Props = {
    corporateActions: CorporateAction[],
    contractSwitches: ContractSwitch[],
    chart: Chart | undefined,
    visibleTimeRange: string,
}

type CorporateActionDisplayProps = {
    action: CorporateAction,
    avatarProps: AvatarProps
}

type CompanyEventDisplayProps = {
    event: CompanyEventProto,
    avatarProps: AvatarProps
}

type ContractSwitchDisplayProps = {
    contractSwitch: ContractSwitch,
    avatarProps: AvatarProps
}

export const getDisplayName = (type: Type): string => {
    switch (type) {
        case Type.BONUS :
            return 'Bonus'
        case Type.SPECIAL_DIVIDEND:
            return 'Dividend'
        case Type.FINAL_DIVIDEND:
            return 'Dividend'
        case Type.RIGHTS:
            return 'Rights'
        case Type.INTERIM_DIVIDEND:
            return 'Dividend'
        case Type.SPLIT:
            return 'Split'
    }
    return 'Corporate Action'
}

const getDisplayLetter = (type: Type): string => {
    switch (type) {
        case Type.BONUS :
            return 'B'
        case Type.SPECIAL_DIVIDEND:
            return 'D'
        case Type.FINAL_DIVIDEND:
            return 'D'
        case Type.RIGHTS:
            return 'R'
        case Type.INTERIM_DIVIDEND:
            return 'D'
        case Type.SPLIT:
            return 'S'
    }
    return 'Corporate Action'
}

const getDisplayDate = (time: number ): string => {
    let timestamp = Number(time);
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
        console.warn("Invalid date:", time);
        return '-';
    }
    const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
    const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${weekday}, ${month} ${day}, ${year}`;
}

const getDisplayDateForCompanyEvent = (time: string): string => {
    const date = new Date(time);
    const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
    const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${weekday}, ${month} ${day}, ${year}`;
}
export const getColorForAction = (type: Type): string => {
    switch (type) {
        case Type.BONUS :
            return '#B8860B'
        case Type.SPECIAL_DIVIDEND:
            return '#0f89e5'
        case Type.FINAL_DIVIDEND:
            return '#0f89e5'
        case Type.RIGHTS:
            return 'rgb(171, 71, 188)'
        case Type.INTERIM_DIVIDEND:
            return '#0f89e5'
        case Type.SPLIT:
            return 'rgb(251, 140, 0)'
    }
    return '#ffffff'
}

export const ActionContent = ({ action }: { action: any }) => {
    const type = typeFromJSON(action.type)
    switch (type) {
        case Type.BONUS: {
            return (
                <>
                    <DisplayRow label="Bonus Ratio" value={`${action.ratioNumerator}:${action.ratioDenominator}`} />
                    <DisplayRow label="Ex Date" value={getDisplayDate(action.exDate)} />
                    {action.announcementDate !== 0 && (
                        <DisplayRow label="Announcement Date" value={getDisplayDate(action.announcementDate)} />
                    )}
                    {action.recordDate !== 0 && (
                        <DisplayRow label="Record Date" value={getDisplayDate(action.recordDate)} />
                    )}
                </>
            )
        }

        case Type.SPECIAL_DIVIDEND:
        case Type.FINAL_DIVIDEND:
        case Type.INTERIM_DIVIDEND: {
            return (
                <>
                    <DisplayRow label="Dividend Amount" value={(action.dividendAmount / 100).toFixed(2)} />
                    <DisplayRow label="Type" value={dividendTypeMap[Type[type].toString()]} />
                    <DisplayRow label="Ex Date" value={getDisplayDate(action.exDate)} />
                    {action.announcementDate !== 0 && (
                        <DisplayRow label="Announcement Date" value={getDisplayDate(action.announcementDate)} />
                    )}
                    {action.recordDate !== 0 && (
                        <DisplayRow label="Record Date" value={getDisplayDate(action.recordDate)} />
                    )}
                </>
            )
        }

        case Type.RIGHTS: {
            return (
                <>
                    <DisplayRow label="Premium" value={(action.rightsPremium / 100).toFixed(2)} />
                    <DisplayRow label="Face Value" value={`${(action.newFaceValue / 100).toFixed(2)}`} />
                    <DisplayRow label="Rights Ratio" value={`${action.ratioNumerator}:${action.ratioDenominator}`} />
                    <DisplayRow label="Ex Date" value={getDisplayDate(action.exDate)} />
                    {action.announcementDate !== 0 && (
                        <DisplayRow label="Announcement Date" value={getDisplayDate(action.announcementDate)} />
                    )}
                    {action.recordDate !== 0 && (
                        <DisplayRow label="Record Date" value={getDisplayDate(action.recordDate)} />
                    )}
                </>
            )
        }

        case Type.SPLIT: {
            return (
                <>
                    <DisplayRow label="Split Ratio" value={`${action.ratioNumerator}:${action.ratioDenominator}`} />
                    <DisplayRow label="Face Value" value={`${(action.newFaceValue / 100).toFixed(2)}`} />
                    <DisplayRow label="Ex Date" value={getDisplayDate(action.exDate)} />
                    {action.announcementDate !== 0 && (
                        <DisplayRow label="Announcement Date" value={getDisplayDate(action.announcementDate)} />
                    )}
                    {action.recordDate !== 0 && (
                        <DisplayRow label="Record Date" value={getDisplayDate(action.recordDate)} />
                    )}
                </>
            )
        }

        default:
            return <></>
    }
}
const DisplayRow = ({ label, value }: { label: string, value: string | number }) => (
    <Box sx={{ display: 'flex', width: '100%' }}>
        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color="textSecondary" fontSize="14px">
            {label}
        </Typography>
        <Typography
            sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
            fontSize="14px"
        >
            {value}
        </Typography>
    </Box>
)
const DisplayRowCompanyEvents = ({label,value}:{label: string, value: string | number}) => (
    <Box sx={{ display: 'flex', width: '100%' }}>
        <Typography sx={{ flexGrow: 0, flexBasis: '30%' }} color="textSecondary" fontSize="14px">
            {label}
        </Typography>
        <Typography
            sx={{ flexGrow: 0, flexBasis: '70%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
            fontSize="14px"
        >
            {value}
        </Typography>
    </Box>
)

export const CorporateActionDisplay = ({ action, avatarProps }: CorporateActionDisplayProps) => {
    const type = typeFromJSON(action.type)
    const color = getColorForAction(type)

    return (
        <Card style={{ borderLeft: `4px solid ${color}`, width: '100%', minWidth: 325}}>
            <CardHeader
                avatar={
                    <Avatar {...avatarProps} aria-label="corporate-action">
                        {getDisplayLetter(type)}
                    </Avatar>
                }
                sx={{pb:0, pt:1}}

                title={
                    <Box>
                        <Typography variant="subtitle1">{getDisplayName(type)}</Typography>
                    </Box>
                }
            />
            <CardContent sx={{ minWidth: 325, display: 'flex', flexDirection: 'column', gap: 1}}>
                <ActionContent action={action} />
            </CardContent>
        </Card>
    )
}

export const CompanyEventDisplay = ({ event,avatarProps}: CompanyEventDisplayProps) => {
    const color = 'skyblue'
    return (
        <Card style={{ borderLeft: `4px solid ${color}`, width: '100%', minWidth: 325 }}>
            <CardHeader
                avatar={
                    <Avatar {...avatarProps} aria-label="corporate-action">
                        E
                    </Avatar>
                }
                sx={{ pb: 0 }}
                title={
                    <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        <Typography variant="subtitle2">
                            {event.displayPurpose}
                        </Typography>
                    </Box>
                }
            />
            <CardContent sx={{ minWidth: 325, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <DisplayRowCompanyEvents label="Date" value={getDisplayDateForCompanyEvent(event.date)} />
                <DisplayRowCompanyEvents label="Details" value={event.details} />
            </CardContent>
        </Card>
    )
}

const ContractSwitchDisplay = ({ contractSwitch, avatarProps }: ContractSwitchDisplayProps) => {

    return (
        <Card style={{ borderLeft: '4px solid rgb(124, 77, 255)' }}>
            <CardHeader
                avatar={
                    <Avatar {...avatarProps} aria-label="contract-switch">
                        <Contract sx={{ fontSize: '13px', ml: '1px' }} />
                    </Avatar>
                }
                title={<Typography variant="h6">{'Contract Switch'}</Typography>}
            />
            <CardContent sx={{ minWidth: 350, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 1 }}>Date</Typography>
                        <Typography sx={{ flexGrow: 1 }}>{getDisplayDate(contractSwitch.time)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 1 }}>From</Typography>
                        <Typography sx={{ flexGrow: 1 }}>{contractSwitch.fromContract}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 1 }}>To</Typography>
                        <Typography sx={{ flexGrow: 1 }}>{contractSwitch.toContract}</Typography>
                    </Box>
                </>
            </CardContent>
        </Card>
    )
}

const ContractualEventItem = memo(({ event, bottomThreshold, index, xPos, type, container }: {
    event: CorporateAction | ContractSwitch,
    bottomThreshold: number,
    index: number,
    xPos: number,
    type: ContractualEventType,
    container: HTMLElement
}) => {
    const color = (type === 'corporateAction') ? getColorForAction((event as CorporateAction).type) : 'rgb(124, 77, 255)'
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
    const clicked = useMemo(() => Boolean(anchorEl), [anchorEl])
    const theme = useTheme()

    const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    return <>
        <Avatar style={{
            width: 20,
            height: 20,
            fontSize: 12,
            position: 'absolute',
            left: `${xPos ? xPos - 10 : -1000}px`,
            bottom: bottomThreshold ? bottomThreshold + (index * 20) : -1000,
            fontWeight: 500,
            zIndex: 5,
            backgroundColor: clicked ? color : theme.palette.background.paper,
            borderColor: color,
            borderWidth: 2,
            outline: `1.5px solid ${theme.palette.background.paper}`,
            color: clicked ? theme.palette.background.paper : color,
            cursor: 'pointer'
        }}
                aria-label="corporate-action-icon"
                onClick={handleClick}

        >
            {(type === 'contractSwitch') ?
                <Contract sx={{ fontSize: '13px', ml: '1px' }} /> : getDisplayLetter((event as CorporateAction).type)}

        </Avatar>
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'center'
            }}
            transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
            }}
            container={container}
        >
            {
                (type === 'corporateAction') ? (
                    <CorporateActionDisplay action={event as CorporateAction} avatarProps={{
                        sx: {
                            width: 24,
                            height: 24,
                            fontSize: 12,
                            backgroundColor: 'transparent',
                            borderColor: color,
                            borderWidth: 1,
                            color: color
                        }

                    }} />) : (
                    <ContractSwitchDisplay contractSwitch={event as ContractSwitch} avatarProps={{
                        sx: {
                            width: 24,
                            height: 24,
                            fontSize: 12,
                            backgroundColor: 'transparent',
                            borderColor: color,
                            borderWidth: 1,
                            color: color
                        }

                    }} />
                )
            }
        </Popover>
    </>
})

const ContractualEvents = ({ corporateActions, chart, visibleTimeRange, contractSwitches }: Props) => {
    const timezone = useSelector(selectTimezone)
    const timeToActions: [number, CorporateAction[]][] = useMemo(() => {
        const mp: Map<number, CorporateAction[]> = new Map()
        corporateActions.map((ca) => {
            mp.set(ca.exDate, [...(mp.get(ca.exDate) ?? []), ca])
        })
        return Array.from(mp)
    }, [corporateActions, visibleTimeRange])

    const timeToSwitches: [number, ContractSwitch[]][] = useMemo(() => {
        const mp: Map<number, ContractSwitch[]> = new Map()
        contractSwitches.map((ca) => {
            mp.set(ca.time, [...(mp.get(ca.time) ?? []), ca])
        })
        return Array.from(mp)
    }, [contractSwitches, visibleTimeRange])

    const calculateXPosition = useCallback((time: number) => {
        if (chart) {
            const viewportStartTime = convertToUTC(chart.viewportCandleInfo.startTime * 1000, timezone)
            const viewportEndTime = convertToUTC(chart.viewportCandleInfo.endTime * 1000, timezone)
            const sd = new Date(viewportStartTime)
            const ed = new Date(viewportEndTime)
            const dt = new Date(time as number)
            sd.setHours(0, 0, 0, 0)
            ed.setHours(0, 0, 0, 0)
            dt.setHours(0, 0, 0, 0)
            if (dt.getTime() < sd.getTime() || dt.getTime() > ed.getTime()) return NaN

            const candles = chart.getRawData();
            const timestamps = candles.map((candle) => {
                const dt = new Date((candle.time as number) * 1000)
                dt.setHours(0, 0, 0, 0)
                return dt.getTime()
            })
            const index = binarySearchFirstDuplicate(timestamps, dt.getTime())
            if (index == -1) return NaN
            const zt = zonedTime(candles[index].time as number, timezone)
            return chart.timeToCoordinate(zt)
        }
        return NaN
    }, [timezone, chart?.timeToCoordinate, chart?.viewportCandleInfo, chart?.dataManager])

    const timeScaleHeight = useMemo(() => {
        if (chart) {
            return chart.getTimeScaleHeight()
        }
        return NaN
    }, [chart])

    return (
        <>
            {
                timeToActions.map((timeAndActions: [number, CorporateAction[]]) => {
                    const xPos = calculateXPosition(timeAndActions[0])
                    return Number.isNaN(xPos) ? null : <>
                    </>
                })
            }
            {
                timeToSwitches.map((timeAndSwitches: [number, ContractSwitch[]]) => {
                    const xPos = calculateXPosition(timeAndSwitches[0])
                    return Number.isNaN(xPos) ? null : <>
                    </>
                })
            }
        </>
    )
}

export default ContractualEvents